import { useTranslate } from "ra-core";
import { useRef, useState, type ChangeEvent } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

import type { RAFile } from "../types";
import type { MailAccount, SignatureInput } from "./mailTypes";
import { useMailAccountMutation } from "./useMailAccount";

const MAX_IMAGE_BYTES = 2 * 1024 * 1024;

/** « Signature » card: text and logo added at the end of sent emails. */
export const SignatureSection = ({
  account,
}: {
  account: MailAccount | null;
}) => {
  const translate = useTranslate();
  const inputRef = useRef<HTMLInputElement>(null);
  const [text, setText] = useState(account?.signature_text ?? "");
  const [image, setImage] = useState<RAFile | null>(
    account?.signature_image ?? null,
  );
  const [isTooBig, setTooBig] = useState(false);

  const save = useMailAccountMutation(
    (dataProvider, input: SignatureInput) =>
      dataProvider.saveMailSignature(input),
    "crm.mail.signature.saved",
  );

  const handleImage = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/") || file.size > MAX_IMAGE_BYTES) {
      setTooBig(true);
      return;
    }
    setTooBig(false);
    setImage({
      rawFile: file,
      src: URL.createObjectURL(file),
      title: file.name,
      type: file.type,
    });
  };

  return (
    <Card>
      <CardContent className="flex flex-col gap-4">
        <div>
          <h2 className="text-lg font-semibold">
            {translate("crm.mail.signature.title")}
          </h2>
          <p className="text-sm text-muted-foreground">
            {translate("crm.mail.signature.help")}
          </p>
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="mail-signature">
            {translate("crm.mail.signature.text")}
          </Label>
          <Textarea
            id="mail-signature"
            rows={5}
            value={text}
            onChange={(event) => setText(event.target.value)}
          />
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            className="hidden"
            aria-label={translate("crm.mail.signature.image")}
            onChange={handleImage}
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => inputRef.current?.click()}
          >
            {translate("crm.mail.signature.image")}
          </Button>
          {image ? (
            <>
              <img
                src={image.src}
                alt=""
                className="max-h-12 max-w-40 object-contain"
              />
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setImage(null)}
              >
                {translate("crm.mail.signature.remove_image")}
              </Button>
            </>
          ) : null}
          {isTooBig ? (
            <span className="text-sm text-destructive">
              {translate("crm.mail.signature.image_invalid")}
            </span>
          ) : null}
        </div>
        <div className="flex justify-end">
          <Button
            type="button"
            disabled={save.isPending}
            onClick={() =>
              save.mutate({
                signature_text: text.trim() || null,
                signature_image: image,
              })
            }
          >
            {translate("crm.mail.signature.save")}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
